import React, { forwardRef } from 'react';
import { areEqual, FixedSizeGrid, GridChildComponentProps } from 'react-window';
import { useRecoilValue } from 'recoil';
import { useOpenPopup } from 'src/modules/imagesV3/hooks/useOpenPopup';
import {
  imageItemSelector,
  pieceItemLogoSelector,
  shopImagesListSelector
} from 'src/modules/imagesV3/store/selector';

import styles from './index.module.scss';
import { LIST_HEIGHT, LIST_WIDTH, RATIO } from './list';
import BaseItem from '../BaseItem';
import SmallText from 'src/components/SmallText';
import { levels } from '../../constants/levels';
import ImageButton from '../ImageButton';
import { types } from '../../constants/types';
import EmptyPlaceholder from '../EmptyPlaceholder';

const calcStyle = (style: any, col: number) => ({
  ...style,
  top: `${parseFloat(style.top) + 10 * RATIO}px`,
  left: `${parseFloat(style.left) + 10 * RATIO}px`
});

const Cell = React.memo(
  ({ style, rowIndex, columnIndex, data }: GridChildComponentProps) => {
    const open = useOpenPopup();
    const item = useRecoilValue(
      imageItemSelector(data[rowIndex * 2 + columnIndex])
    );
    const pieceLogo = useRecoilValue(
      pieceItemLogoSelector(item?.piecesType || 0)
    );
    if (!item) return null;
    return (
      <div style={calcStyle(style, columnIndex)} className={styles.wrapper}>
        <BaseItem
          title={item.title}
          logo={item.url}
          renderDesc={() => {
            const list = [1, 6, 8];
            return list.includes(item.type) ? (
              <span className={styles.noble}>{item.desc}</span>
            ) : (
              <span className={styles.price}>
                <span
                  style={{ backgroundImage: `url(${pieceLogo})` }}
                  className={styles.piece}
                />
                {item.piecesExchange}
              </span>
            );
          }}
          renderFooter={() => {
            if (item.type === 1 || item.type === 8 || item.type === 6) {
              return null;
            }
            return (
              <ImageButton
                type="primary"
                onClick={() => {
                  if (item.type === 6) {
                    window.location.href = 'oupai://user/noble/info';
                  } else {
                    open('exchange', item.id, 2);
                  }
                }}
              >
                {item.type === 6 ? '前往' : '兑换'}
              </ImageButton>
            );
          }}
          renderLabels={() => (
            <div className={styles.labels}>
              <SmallText fontSize={18} className={styles.type} inline>
                {types[item.type]}
              </SmallText>
              {item.level > 0 && (
                <SmallText fontSize={18} className={styles.level} inline>
                  {levels(item.level)}
                </SmallText>
              )}
            </div>
          )}
        />
      </div>
    );
  },
  areEqual
);

const InnerElementType: React.FC = forwardRef<any, any>(
  ({ style, ...rest }, ref) => (
    <div
      ref={ref}
      style={{
        ...style,
        height: `${parseFloat(style.height) + 20 * RATIO}px`
      }}
      {...rest}
    />
  )
);

const ShopImages: React.FC = () => {
  const list = useRecoilValue(shopImagesListSelector);
  if (list.length === 0) return <EmptyPlaceholder />;
  return (
    <FixedSizeGrid
      className={styles.container}
      columnCount={2}
      columnWidth={365 * RATIO}
      height={LIST_HEIGHT}
      rowCount={Math.ceil(list.length / 2)}
      rowHeight={465 * RATIO}
      width={LIST_WIDTH}
      itemData={list}
      innerElementType={InnerElementType}
    >
      {Cell}
    </FixedSizeGrid>
  );
};

export default React.memo(ShopImages);
